<template>
  <div class="page">
    <!-- 헤더 -->
    <header class="top-header">
      <div class="header-inner">
        <div class="header-left">
          <div class="brand-icon">
            <img
              src="@/assets/icon/tech-center-icon.jpg"
              alt="테크센터 아이콘"
            />
          </div>
          <div class="brand-text">
            <div class="brand-title">부산광역시교육청 테크센터</div>
            <div class="brand-sub">(주) 디아이앤씨</div>
          </div>
        </div>

        <nav class="header-center">
          <button
            type="button"
            class="menu-btn"
            :class="{ active: activeMenu === 'lookup' }"
            @click="goLookup"
          >
            A/S조회
          </button>
          <button
            type="button"
            class="menu-btn"
            :class="{ active: activeMenu === 'faq' }"
            @click="activeMenu = 'faq'"
          >
            FAQ
          </button>
        </nav>

        <div class="header-right"></div>
      </div>
    </header>

    <!-- 컨텐츠 -->
    <main class="content-wrap">
      <section class="content-card">
        <div class="content-head">
          <h1 class="page-title">FAQ</h1>
        </div>

        <section class="result-panel">
          <div class="result-top">
            <div>
              <!-- <h2 class="result-title">FAQ 목록</h2> -->
              <p class="result-desc">자주 문의하시는 내용을 확인해보세요.</p>
            </div>
          </div>

          <!-- 데스크톱 테이블 -->
          <div class="desktop-only">
            <div class="table-wrap">
              <table class="faq-table">
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>제목</th>
                    <th>구분</th>
                  </tr>
                </thead>

                <tbody>
                  <template v-if="faqs.length">
                    <template v-for="(item, index) in faqs" :key="index">
                      <tr
                        class="data-row"
                        :class="{ active: openIndex === index }"
                        @click="toggleFaq(index)"
                      >
                        <td>{{ index + 1 }}</td>
                        <td>
                          <div class="faq-title-cell">
                            <span class="q-badge">Q</span>
                            <span class="ellipsis">{{ item.question }}</span>
                          </div>
                        </td>
                        <td>
                          <span class="status-chip default">
                            {{ item.category || "안내" }}
                          </span>
                        </td>
                      </tr>

                      <tr v-if="openIndex === index" class="answer-row">
                        <td colspan="3">
                          <div class="faq-answer-box">
                            <div class="faq-answer-head">
                              <span class="a-badge">A</span>
                              <strong>{{ item.question }}</strong>
                            </div>
                            <div
                              class="faq-answer-body"
                              v-html="item.answer"
                            ></div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>

                  <tr v-else>
                    <td colspan="3" class="empty-row">
                      등록된 FAQ가 없습니다.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 모바일 카드 -->
          <div class="mobile-only">
            <div class="mobile-list">
              <template v-if="faqs.length">
                <article
                  v-for="(item, index) in faqs"
                  :key="index"
                  class="mobile-item"
                  @click="toggleFaq(index)"
                >
                  <div class="mobile-top">
                    <div class="mobile-school faq-mobile-title">
                      <span class="q-badge">Q</span>
                      <span>{{ item.question }}</span>
                    </div>
                    <span class="status-chip default">
                      {{ item.category || "안내" }}
                    </span>
                  </div>

                  <transition name="faq-slide">
                    <div v-if="openIndex === index" class="mobile-answer">
                      <div class="faq-answer-head">
                        <span class="a-badge">A</span>
                        <strong>{{ item.question }}</strong>
                      </div>
                      <div class="faq-answer-body" v-html="item.answer"></div>
                    </div>
                  </transition>
                </article>
              </template>

              <div v-else class="empty-mobile">등록된 FAQ가 없습니다.</div>
            </div>
          </div>
        </section>
      </section>
    </main>

    <!-- 우측 fixed 카카오채널 -->
    <aside class="side-floating-menu">
      <a
        href="https://pf.kakao.com/_ttmMn"
        target="_blank"
        rel="noopener noreferrer"
        class="floating-item"
        title="카카오채널 바로가기"
      >
        <div class="floating-icon floating-icon--qr">
          <img src="@/assets/icon/qrcode_120.png" alt="카카오채널 QR코드" />
        </div>
        <div class="floating-label">카카오채널</div>
      </a>
    </aside>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const activeMenu = ref("faq");
const openIndex = ref(-1);

function goLookup() {
  router.push({ name: "Lookup" });
}

function toggleFaq(index) {
  openIndex.value = openIndex.value === index ? -1 : index;
}

const faqs = ref([
  {
    category: "전자칠판",
    question: "전자칠판 태블릿 PC 설정",
    answer: `
      <div class="video-wrap">
        <iframe
          title="1. 전자칠판 태블릿 PC 설정"
          src="https://play-tv.kakao.com/embed/player/cliplink/454259692?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "전자칠판",
    question: "전자칠판 EDID 변경 방법",
    answer: `
      <div class="video-wrap">
        <iframe
          title="2. 전자칠판 EDID 변경 방법"
          src="https://play-tv.kakao.com/embed/player/cliplink/454259716?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "전자칠판",
    question: "전자칠판 미러링 연결하기",
    answer: `
      <div class="video-wrap">
        <iframe
          title="3. 전자칠판 미러링 연결하기"
          src="https://play-tv.kakao.com/embed/player/cliplink/454259852?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "전자칠판",
    question: "전자칠판 외부입력 이름 변경하기",
    answer: `
      <div class="video-wrap">
        <iframe
          title="4. 전자칠판 외부입력 이름 변경하기"
          src="https://play-tv.kakao.com/embed/player/cliplink/454259892?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "빔프로젝터",
    question: "빔프로젝터 포커스 조정하기",
    answer: `
      <div class="video-wrap">
        <iframe
          title="5. 빔프로젝터 포커스 조정하기"
          src="https://play-tv.kakao.com/embed/player/cliplink/454272331?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "빔프로젝터",
    question: "빔프로젝터 키스톤 보정하기",
    answer: `
      <div class="video-wrap">
        <iframe
          title="6. 빔프로젝터 키스톤 보정하기"
          src="https://play-tv.kakao.com/embed/player/cliplink/454272390?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "빔프로젝터",
    question: "빔프로젝터 터치 보정하기",
    answer: `
      <div class="video-wrap">
        <iframe
          title="7. 빔프로젝터 터치 보정하기"
          src="https://play-tv.kakao.com/embed/player/cliplink/454272447?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "태블릿",
    question: "안드로이드 태블릿 초기화 방법",
    answer: `
      <div class="video-wrap">
        <iframe
          title="8. 안드로이드 태블릿 초기화 방법"
          src="https://play-tv.kakao.com/embed/player/cliplink/459487954?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "태블릿",
    question: "안드로이드 태블릿 리커버리 모드 초기화",
    answer: `
      <div class="video-wrap">
        <iframe
          title="9. 안드로이드 태블릿 리커버리 모드 초기화"
          src="https://play-tv.kakao.com/embed/player/cliplink/459488115?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "태블릿",
    question: "안드로이드 태블릿 MDM 설치 방법",
    answer: `
      <div class="video-wrap">
        <iframe
          title="10. 안드로이드 태블릿 MDM 설치 방법"
          src="https://play-tv.kakao.com/embed/player/cliplink/459488180?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  },
  {
    category: "태블릿",
    question: "안드로이드 태블릿 Talk Back 설정 해제 방법",
    answer: `
      <div class="video-wrap">
        <iframe
          title="11. 안드로이드 태블릿 Talk Back 설정 해제 방법"
          src="https://play-tv.kakao.com/embed/player/cliplink/459488312?service=player_share"
          allowfullscreen
          frameborder="0"
          scrolling="no"
          allow="autoplay; fullscreen; encrypted-media">
        </iframe>
      </div>
    `
  }
]);
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: #f6f7f9;
  color: #1f2937;
}

/* 헤더 */
.top-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.header-inner {
  max-width: 1440px;
  height: 96px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.brand-icon {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-title {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}

.brand-sub {
  font-size: 13px;
  color: #6b7280;
  line-height: 1;
}

.header-center {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: center;
}

.menu-btn {
  height: 44px;
  padding: 0 22px;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #4b5563;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
}

.menu-btn.active {
  color: #111827;
  border-bottom-color: #18a0fb;
}

.header-right {
  min-height: 1px;
}

/* 컨텐츠 */
.content-wrap {
  max-width: 1440px;
  margin: 0 auto;
  padding: 48px 24px 60px;
}

.content-card {
  min-height: 680px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  padding: 52px 56px;
}

.content-head {
  margin-bottom: 28px;
}

.page-title {
  margin: 0;
  font-size: 48px;
  font-weight: 800;
  color: #111827;
  line-height: 1.15;
}

/* 결과 패널 */
.result-panel {
  padding-top: 12px;
}

.result-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 20px;
}

.result-title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #111827;
}

.result-desc {
  margin: 8px 0 0;
  font-size: 15px;
  color: #6b7280;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none !important;
}

.table-wrap {
  overflow-x: auto;
  border-top: 2px solid #111827;
}

.faq-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.faq-table thead th {
  background: #f9fafb;
  color: #374151;
  font-size: 14px;
  font-weight: 800;
  text-align: left;
  padding: 16px 14px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.faq-table tbody td {
  padding: 16px 14px;
  border-bottom: 1px solid #eef2f7;
  vertical-align: middle;
  font-size: 14px;
  background: #ffffff;
}

.faq-table th:nth-child(1),
.faq-table td:nth-child(1) {
  width: 90px;
  text-align: center;
}

.faq-table th:nth-child(2),
.faq-table td:nth-child(2) {
  width: auto;
}

.faq-table th:nth-child(3),
.faq-table td:nth-child(3) {
  width: 140px;
  text-align: center;
}

.faq-title-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-row {
  cursor: pointer;
  transition: background 0.2s ease;
}

.data-row:hover td {
  background: #f8fafc;
}

.data-row.active td {
  background: #eef6ff;
}

.answer-row td {
  padding: 0;
  background: #fbfcfe;
}

.faq-answer-box {
  padding: 22px 24px;
  border-top: 1px solid #e5edf7;
}

.faq-answer-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  color: #111827;
}

.faq-answer-body {
  color: #374151;
  line-height: 1.7;
}

.q-badge,
.a-badge {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  flex-shrink: 0;
}

.q-badge {
  background: #eff6ff;
  color: #2563eb;
}

.a-badge {
  background: #ecfdf5;
  color: #059669;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.status-chip.default {
  background: #f1f5f9;
  color: #475569;
}

.empty-row {
  text-align: center;
  color: #6b7280;
  padding: 36px 16px !important;
}

/* 모바일 */
.mobile-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-item {
  padding: 16px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
}

.mobile-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.faq-mobile-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
  color: #111827;
}

.mobile-answer {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eef2f7;
}

.empty-mobile {
  padding: 24px 16px;
  text-align: center;
  color: #6b7280;
}

/* 우측 fixed 카카오채널 */
.side-floating-menu {
  position: fixed;
  top: 50%;
  left: calc(50% + 720px + ((100vw - 1440px) / 4) - 43px);
  transform: translateY(-50%);
  z-index: 40;
  width: 86px;
  display: none;
  flex-direction: column;
  align-items: center;
}

.floating-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
}

.floating-icon {
  width: 86px;
  height: 86px;
  border-radius: 999px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.floating-icon--qr {
  background: #fee500;
  border-color: #f2d600;
}

.floating-icon--qr img {
  width: 50px;
  height: 50px;
  object-fit: contain;
  display: block;
}

.floating-label {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #374151;
  text-align: center;
  line-height: 1.35;
  white-space: nowrap;
}

@media (min-width: 1526px) {
  .side-floating-menu {
    display: flex;
  }
}

/* 비디오 */
:deep(.video-wrap) {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 12px;
  background: #000;
}

:deep(.video-wrap iframe) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* transition */
.faq-slide-enter-active,
.faq-slide-leave-active {
  transition: all 0.22s ease;
}

.faq-slide-enter-from,
.faq-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 1024px) {
  .header-inner {
    grid-template-columns: 1fr;
    gap: 14px;
    height: auto;
    padding: 16px;
  }

  .header-center {
    justify-self: start;
  }

  .content-wrap {
    padding: 24px 16px 40px;
  }

  .content-card {
    padding: 28px 20px;
  }

  .page-title {
    font-size: 34px;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .side-floating-menu {
    display: none !important;
  }
}

@media (max-width: 640px) {
  .brand-title {
    font-size: 18px;
  }

  .brand-sub {
    font-size: 12px;
  }

  .menu-btn {
    padding: 0 12px;
    font-size: 15px;
  }

  .page-title {
    font-size: 28px;
  }

  .result-title {
    font-size: 22px;
  }

  .result-desc {
    font-size: 14px;
  }
}
</style>
